import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserMapping } from '../model/usermapping';
import { List } from 'immutable';
import { AppConfig } from '../model/config';
import { HttpHelper } from './http.service';
import { UserCheckIn } from '../model/userCheckIn';
import { SignalRService } from './signalr.service';


@Injectable()
export class UserMappingService {

  private readonly userMappings: BehaviorSubject<List<UserMapping>> = new BehaviorSubject(List([]));
  public readonly UserMappings = this.userMappings.asObservable();

  private readonly checkedInUserInfo: BehaviorSubject<List<UserCheckIn>> = new BehaviorSubject(List([]));
  public readonly CheckedInUsers = this.checkedInUserInfo.asObservable();

  constructor(
    private readonly http: HttpHelper,
    private readonly configuration: AppConfig,
    private readonly zone: NgZone,
    private readonly signalR: SignalRService) {

    this.signalR.checkedin$.subscribe((userCheckIn: UserCheckIn) => {
      this.updateUsermappingWithCheckedIn(new UserCheckIn(userCheckIn));
    });

    this.signalR.checkedout$.subscribe((userCheckIn: UserCheckIn) => {
      this.updateUsermappingWithCheckedIn(new UserCheckIn(userCheckIn));
    });
  }

  private updateUsermappingWithCheckedIn(userCheckIn: UserCheckIn) {

    this.updateCheckedInUserList(userCheckIn);

    const ums = this.userMappings.getValue();
    const index = ums.findIndex((um: UserMapping) => um.id === userCheckIn.usermappingId);

    if (index === -1) return;

    const um: UserMapping = ums.get(index);
    um.userCheckIn = userCheckIn;
    this.userMappings.next(ums.set(index, um));
  }

  private updateCheckedInUserList(userCheckIn: UserCheckIn) {

    let checkedInUserInfo = this.checkedInUserInfo.getValue();
    const index = checkedInUserInfo.findIndex((uc: UserCheckIn) => uc.usermappingId === userCheckIn.usermappingId);

    if (index === -1) {
      checkedInUserInfo = checkedInUserInfo.push(userCheckIn);
    }
    else {
      checkedInUserInfo = checkedInUserInfo.set(index, userCheckIn);
    }

    this.checkedInUserInfo.next(checkedInUserInfo);

  }

  public userCheckInOrOut(usermappingId: string, checkIn: boolean)  {

    if (checkIn) {
      return this.http.post(this.configuration.backendUrl + "/api/UserInfo/CheckIn?usermappingId=" + usermappingId, null,
        (result: UserCheckIn) => {
          const ums = this.userMappings.getValue();
          const index = ums.findIndex((um: UserMapping) => um.id === result.usermappingId);
          const um: UserMapping = ums.get(index);
          um.userCheckIn = new UserCheckIn(result);
          this.zone.run(() => this.userMappings.next(ums.set(index, um)));
        },
        (exception) => { }).toPromise<any>();
    }
    else {
      return this.http.post(this.configuration.backendUrl + "/api/UserInfo/CheckOut?usermappingId=" + usermappingId, null,
        (result: UserCheckIn) => {
          const ums = this.userMappings.getValue();
          const index = ums.findIndex((um: UserMapping) => um.id === result.usermappingId);
          const um: UserMapping = ums.get(index);
          um.userCheckIn = new UserCheckIn(result);
          this.zone.run(() => this.userMappings.next(ums.set(index, um)));
        },
        (exception) => { }).toPromise<any>();
    }
  }

  public async getAllUserMappings(): Promise<UserMapping[]> {

    // usermappings
    return await this.http.get(this.configuration.backendUrl + "/api/usermapping/all", (data: UserMapping[]) => {
      let userMappings = List<UserMapping>();
      data.forEach((userMapping) => {
        userMappings = userMappings.push(new UserMapping(userMapping));
        if (userMapping.userCheckIn !== undefined && userMapping.userCheckIn !== null) {
          this.updateCheckedInUserList(new UserCheckIn(userMapping.userCheckIn));
        }
      });
      this.zone.run(() => {

        this.userMappings.next(userMappings)
      });


    }, (error) => { }).toPromise<any>();
  }

  public async loadAllCheckedInUsers() {

    await this.http.get(this.configuration.backendUrl + "/api/usermapping/checkedIn", (data: UserCheckIn[]) => {
      data.forEach((checkedInUser) => {
        this.updateCheckedInUserList(new UserCheckIn(checkedInUser));
      });
      
    }, (error) => { }).toPromise<any>();
  }

  // Create UserMapping and User in AD
  async createUser(userMapping: any): Promise<any> {

    let createdUserMapping: UserMapping;

    return this.http.put(this.configuration.backendUrl + "/api/usradm/createUser", userMapping,
      (result) => {

        userMapping = result.result;
        createdUserMapping = new UserMapping(result.result as UserMapping);
        this.zone.run(() => this.userMappings.next(this.userMappings.getValue().push(createdUserMapping)));
      },
      (exception) => { }).toPromise<any>();
  }

  // Update UserMapping and User in AD
  async updateUser(user: any): Promise<any> {

    return this.http.put(this.configuration.backendUrl + "/api/usradm/updateUser", user,
      (result) => {

        const ums = this.userMappings.getValue();
        const index = ums.findIndex((um: UserMapping) => um.id === user.id);
        this.zone.run(() => {

          const usermapping = new UserMapping(result as UserMapping);
          const checkedInInfo = this.checkedInUserInfo.getValue().find(u => u?.usermappingId == usermapping.id);

          if (checkedInInfo) {
            usermapping.userCheckIn = checkedInInfo;
          }

          this.userMappings.next(ums.set(index, usermapping))
        });
      },
      (exception) => { }).toPromise<any>();
  }

  // Get unique DisplayName and login
  async getUniqueIdentity(name: string): Promise<{ name: string, loginId: string }> {

    let url: string = this.configuration.backendUrl + "/api/usradm/getUniqueIdentity";

    return await this.http.get(url, (result) => { },
      (error) => { }).toPromise<{ name: string, loginId: string }>();
  }

  // Get accountEnabled
  async getEnabled(objectId: string): Promise<boolean> {

    let url: string = this.configuration.backendUrl + "/api/usradm/getEnabled";

    return await this.http.get(url, (result) => { },
      (error) => { }).toPromise<boolean>();
  }

  // Has SocialSecurityNumber been used?
  async employmentNumberExists(number: string): Promise<boolean> {

    let url: string = this.configuration.backendUrl + "/api/usradm/employmentNumberExists";

    return await this.http.get(url, (result) => { },
      (error) => { }).toPromise<boolean>();
  }

  // Reset user password
  async resetUserPassword(objectId: string): Promise<any> {

    let url: string = this.configuration.backendUrl + "/api/usradm/resetUserPassword?id=" + objectId;

    return await this.http.get(url, (result) => { },
      (error) => { }).toPromise<any>();
  }

  async deleteUser(objectId: string): Promise<any> {

    let url: string = this.configuration.backendUrl + "/api/usradm/deleteUser?id=" + objectId;

    return await this.http.delete(url, (result) => {

      const ums = this.userMappings.getValue();
      const index = ums.findIndex((um: UserMapping) => um.id === objectId);
      this.zone.run(() => this.userMappings.next(ums.delete(index)));

    },
      (error) => { }).toPromise<any>();
  }

  // Get accountEnabled and group memberships
  async getUserInfo(objectId: string): Promise<any> {

    let url: string = this.configuration.backendUrl + "/api/usradm/getUserInfo?Id=" + objectId;

    return await this.http.get(url, (result) => { },
      (error) => { }).toPromise<any>();
  }

}
